import { defineStore } from 'pinia'
import { RoleTypeEnum } from '@/static/enums'

type AccessesState = {
  canAccessXSF: boolean
  canAccessXSFForCreatingTicket: boolean

  canAccessTIPS: boolean
  canAccessTIPSWithoutAdmin: boolean
  canAccessTIPSAsTS: boolean
  canAccessTIPSAsTSAndAdmin: boolean
  canAccessTIPSAsTsAndManagerAndAdmin: boolean
  canAccessTIPSAsTSAndManager: boolean
  canAccessTipsAsSectionLeader: boolean
  canAccessTIPSAsAdmin: boolean
  canAccessTIPSAsDealer: boolean
  canAccessTIPSWithoutDealer: boolean
  canAccessTIPSDataCard: boolean
  canAccessTIPSVehicleQuery: boolean
  canAccessTIPSAsTSAndFTS: boolean
  canAccessTIPSAsFTS: boolean
  canAccessTIPSWithoutFTS: boolean

  canAccessAsTSEngineer: boolean
  canAccessAsTSManager: boolean
  canAccessAsDealer: boolean
  canAccessAsParts: boolean
  canAccessAsHelpdesk: boolean
  canAccessAsCallCenter: boolean
  canAccessAsCoordinator: boolean

  canAccessSearch: boolean
  canAccessTpr: boolean
  canAccessFrp: boolean
  canAccessAsCoordinatorAndSectionLeaderAndManager: boolean
  canAccessAsCoordinatorAndManager: boolean

  canAccessAsRSDAdministrator: boolean
  canAccessTIPSAsReport: boolean
}

function containsAny(arr1: string[], arr2: string[]): boolean {
  return arr1.some((r) => arr2.includes(r))
}

export const useAccessesStore = defineStore<
  'ACCESSES',
  AccessesState,
  {},
  { setAccesses: (roles: RoleTypeEnum[]) => void; clearAccesses: () => void }
>('ACCESSES', {
  state: () => ({
    // XSF
    canAccessXSF: false,
    // XSF 允许创建 Ticket 的权限
    canAccessXSFForCreatingTicket: false,

    // TIPS
    canAccessTIPS: false,
    // 除了 Admin 访问 TIPS
    canAccessTIPSWithoutAdmin: false,
    // 作为 TS(包含工程师 + section leader) 访问 TIPS
    canAccessTIPSAsTS: false,
    // 作为 TS(包含工程师 + section leader + manager) 访问 TIPS
    canAccessTIPSAsTSAndManager: false,
    // 作为 TS 或者 Admin 访问TIPS
    canAccessTIPSAsTSAndAdmin: false,
    // 作为 TS 或者 Admin 或者Manager 访问TIPS
    canAccessTIPSAsTsAndManagerAndAdmin: false,
    // 作为 Section Leader 访问 TIPS
    canAccessTipsAsSectionLeader: false,
    // 仅允许 Admin 访问 TIPS
    canAccessTIPSAsAdmin: false,
    // 作为 Dealer 访问 TIPS
    canAccessTIPSAsDealer: false,
    // 除了 Dealer 角色以外允许访问 TIPS
    canAccessTIPSWithoutDealer: false,
    // 允许访问 DataCard 页面
    canAccessTIPSDataCard: false,
    // 允许访问 VehicleQuery 页面
    canAccessTIPSVehicleQuery: false,
    // 允许 TS 和 FTS 同时访问 TIPS
    canAccessTIPSAsTSAndFTS: false,
    // 仅允许 FTS 访问 TIPS
    canAccessTIPSAsFTS: false,
    //  允许除了FTS以外的其他角色
    canAccessTIPSWithoutFTS: false,
    // Common
    // 仅作为 Parts 角色访问
    canAccessAsParts: false,
    // 仅作为 Dealer 角色访问
    canAccessAsDealer: false,
    // 仅作为 Helpdesk 角色访问
    canAccessAsHelpdesk: false,
    // 仅作为 TSManager 角色访问
    canAccessAsTSManager: false,
    // 仅作为 TSEngineer 角色访问
    canAccessAsTSEngineer: false,
    // 仅作为 CallCenter 角色访问
    canAccessAsCallCenter: false,
    // 仅作为 Coordinator 角色访问
    canAccessAsCoordinator: false,
    // 仅作为 Coordinator 和 TSEngineer 和 Section Leader 和 manager 和 admin 访问
    canAccessSearch: false,
    // 仅作为 Coordinator 和 TSEngineer 和 Section Leader 和 manager 和 admin 访问
    canAccessTpr: false,
    // 仅作为 Coordinator 和 TSEngineer 和 Section Leader 和 manager 访问
    canAccessFrp: false,
    // 仅作为 Coordinator 和 Section Leader 和 manager 访问
    canAccessAsCoordinatorAndSectionLeaderAndManager: false,
    // 仅作为 Coordinator 和 manager 访问
    canAccessAsCoordinatorAndManager: false,

    // RSD管理员
    canAccessAsRSDAdministrator: false,
    // 报表权限
    canAccessTIPSAsReport: false,
  }),
  actions: {
    setAccesses(roles: RoleTypeEnum[]) {
      if (roles?.length) {
        this.canAccessXSF = containsAny(roles, [
          RoleTypeEnum.Dealer,
          RoleTypeEnum.Helpdesk,
          RoleTypeEnum.Parts,
          RoleTypeEnum.CallCenter,
        ])
        this.canAccessXSFForCreatingTicket = containsAny(roles, [
          RoleTypeEnum.Dealer,
          RoleTypeEnum.CallCenter,
        ])

        this.canAccessTIPSWithoutFTS = containsAny(roles, [
          RoleTypeEnum.TIPS_DEALER,
          RoleTypeEnum.TIPS_ADMIN,
          RoleTypeEnum.TSManager,
          RoleTypeEnum.TSEngineer,
          RoleTypeEnum.Manager,
        ])
        this.canAccessTIPS = containsAny(roles, [
          RoleTypeEnum.TIPS_DEALER,
          RoleTypeEnum.TIPS_ADMIN,
          RoleTypeEnum.TSManager,
          RoleTypeEnum.FTSEngineer,
          RoleTypeEnum.TSEngineer,
          RoleTypeEnum.Coordinator,
          RoleTypeEnum.Manager,
        ])
        this.canAccessTIPSWithoutAdmin = containsAny(roles, [
          RoleTypeEnum.TIPS_DEALER,
          RoleTypeEnum.TSManager,
          RoleTypeEnum.TSEngineer,
        ])
        this.canAccessTIPSAsTS = containsAny(roles, [
          RoleTypeEnum.TSManager,
          RoleTypeEnum.TSEngineer,
        ])
        this.canAccessTIPSAsTSAndManager = containsAny(roles, [
          RoleTypeEnum.TSManager,
          RoleTypeEnum.TSEngineer,
          RoleTypeEnum.Manager,
        ])
        this.canAccessTIPSAsTSAndAdmin = containsAny(roles, [
          RoleTypeEnum.TSManager,
          RoleTypeEnum.TSEngineer,
          RoleTypeEnum.TIPS_ADMIN,
        ])
        this.canAccessTIPSAsTsAndManagerAndAdmin = containsAny(roles, [
          RoleTypeEnum.TSManager,
          RoleTypeEnum.TSEngineer,
          RoleTypeEnum.TIPS_ADMIN,
          RoleTypeEnum.Manager,
        ])
        this.canAccessTipsAsSectionLeader = containsAny(roles, [RoleTypeEnum.TSManager])
        this.canAccessTIPSAsAdmin = containsAny(roles, [RoleTypeEnum.TIPS_ADMIN])
        this.canAccessTIPSAsDealer = containsAny(roles, [RoleTypeEnum.TIPS_DEALER])
        this.canAccessTIPSWithoutDealer = containsAny(roles, [
          RoleTypeEnum.TSManager,
          RoleTypeEnum.TSEngineer,
          RoleTypeEnum.TIPS_ADMIN,
          RoleTypeEnum.FTSEngineer,
        ])
        this.canAccessTIPSDataCard = containsAny(roles, [RoleTypeEnum.TIPS_DEALER])
        this.canAccessTIPSVehicleQuery = containsAny(roles, [RoleTypeEnum.TIPS_DEALER])
        this.canAccessTIPSAsTSAndFTS = containsAny(roles, [
          RoleTypeEnum.TSManager,
          RoleTypeEnum.TSEngineer,
          RoleTypeEnum.FTSEngineer,
        ])
        this.canAccessTIPSAsFTS = containsAny(roles, [RoleTypeEnum.FTSEngineer])

        this.canAccessAsParts = containsAny(roles, [RoleTypeEnum.Parts])
        this.canAccessAsDealer = containsAny(roles, [RoleTypeEnum.Dealer])
        this.canAccessAsHelpdesk = containsAny(roles, [RoleTypeEnum.Helpdesk])
        this.canAccessAsCallCenter = containsAny(roles, [RoleTypeEnum.CallCenter])
        this.canAccessAsCoordinator = containsAny(roles, [RoleTypeEnum.Coordinator])
        this.canAccessAsTSManager = containsAny(roles, [RoleTypeEnum.TSManager])
        this.canAccessAsTSEngineer = containsAny(roles, [RoleTypeEnum.TSEngineer])
        this.canAccessSearch =
          containsAny(roles, [RoleTypeEnum.Coordinator, RoleTypeEnum.Manager]) ||
          this.canAccessTIPSAsTSAndAdmin

        this.canAccessTpr =
          containsAny(roles, [RoleTypeEnum.Coordinator, RoleTypeEnum.Manager]) ||
          this.canAccessTIPSAsTSAndAdmin

        this.canAccessFrp =
          containsAny(roles, [RoleTypeEnum.Coordinator, RoleTypeEnum.Manager]) ||
          this.canAccessTIPSAsTS

        this.canAccessAsCoordinatorAndSectionLeaderAndManager = containsAny(roles, [
          RoleTypeEnum.Coordinator,
          RoleTypeEnum.TSManager,
          RoleTypeEnum.Manager,
        ])
        this.canAccessAsCoordinatorAndManager = containsAny(roles, [
          RoleTypeEnum.Coordinator,
          RoleTypeEnum.Manager,
        ])

        this.canAccessAsRSDAdministrator = containsAny(roles, [RoleTypeEnum.RSDAdministrator])
        this.canAccessTIPSAsReport = containsAny(roles, [
          RoleTypeEnum.TIPS_ADMIN,
          RoleTypeEnum.Coordinator,
          RoleTypeEnum.Manager,
        ])
      }
    },
    clearAccesses() {
      this.canAccessXSF = false
      this.canAccessXSFForCreatingTicket = false

      this.canAccessTIPS = false
      this.canAccessTIPSWithoutAdmin = false
      this.canAccessTIPSAsTS = false
      this.canAccessTIPSAsTSAndManager = false
      this.canAccessTIPSAsTSAndAdmin = false
      this.canAccessTIPSAsTsAndManagerAndAdmin = false
      this.canAccessTipsAsSectionLeader = false
      this.canAccessTIPSAsAdmin = false
      this.canAccessTIPSAsDealer = false
      this.canAccessTIPSWithoutDealer = false
      this.canAccessTIPSDataCard = false
      this.canAccessTIPSVehicleQuery = false
      this.canAccessTIPSAsTSAndFTS = false
      this.canAccessTIPSAsFTS = false

      this.canAccessAsTSEngineer = false
      this.canAccessAsTSManager = false
      this.canAccessAsDealer = false
      this.canAccessAsParts = false
      this.canAccessAsHelpdesk = false
      this.canAccessAsCallCenter = false
      this.canAccessAsCoordinator = false

      this.canAccessSearch = false
      this.canAccessTpr = false
      this.canAccessFrp = false
      this.canAccessAsCoordinatorAndSectionLeaderAndManager = false
      this.canAccessAsCoordinatorAndManager = false

      this.canAccessAsRSDAdministrator = false
      this.canAccessTIPSAsReport = false
    },
  },
})
